import { useState } from 'react';
import { Calendar } from '../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Button } from '../ui/button';
import { CalendarIcon } from 'lucide-react';
import { DateRange } from 'react-day-picker';
import { cn } from '../ui/utils';

interface DateRangePickerProps {
  value: DateRange | undefined;
  onChange: (range: DateRange | undefined) => void;
  className?: string;
  showDateText?: boolean;
}

export function DateRangePicker({ value, onChange, className, showDateText = true }: DateRangePickerProps) {
  const [open, setOpen] = useState(false);

  const formatDate = (date: Date): string => {
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const formatDateRange = () => {
    if (!value) return 'Select date range';
    if (value.from && value.to) {
      return `${formatDate(value.from)} - ${formatDate(value.to)}`;
    }
    if (value.from) {
      return formatDate(value.from);
    }
    return 'Select date range';
  };

  return (
    <div className={cn("relative", className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className={cn(
              "inline-flex items-center justify-center gap-2",
              showDateText ? "px-4 py-2 min-w-[280px]" : "p-2.5 w-10 h-10",
              "rounded-lg",
              "bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm",
              "border border-gray-300/50 dark:border-gray-600/50",
              "hover:bg-white/95 dark:hover:bg-gray-800/95",
              "hover:border-purple-400/50 dark:hover:border-purple-500/50",
              "transition-all duration-200",
              "shadow-sm hover:shadow-md",
              "focus:outline-none focus:ring-2 focus:ring-purple-400/50",
            )}
          >
            <CalendarIcon className="h-4 w-4 shrink-0 text-gray-600 dark:text-gray-400" />
            {showDateText && (
              <span className="font-['SF_Pro_Text',_-apple-system,_sans-serif] text-sm">
                {formatDateRange()}
              </span>
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent 
          className={cn(
            "w-auto p-4",
            "bg-white/95 dark:bg-gray-900/95 backdrop-blur-2xl",
            "border border-gray-200/50 dark:border-gray-700/50",
            "shadow-2xl rounded-2xl"
          )}
          align="end"
          sideOffset={8}
        >
          <Calendar
            mode="range"
            selected={value}
            onSelect={(range) => {
              onChange(range);
              // Close popover when both dates are selected
              if (range?.from && range?.to) {
                setTimeout(() => setOpen(false), 100);
              }
            }}
            numberOfMonths={2}
            initialFocus
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
